import {ChevronDown} from 'lucide-react'
import React from 'react'
import {useNavigate} from 'react-router-dom'
import {
	Avatar,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
	SidebarMenuButton,
} from 'ui-v2'

type WorkspaceSwitcherProps = {
	value?: string
	options: {
		value: string
		label: string
	}[]
	isSwitching?: boolean
	onSelect: (value: string) => void
}

type AvatarColor =
	| 'green'
	| 'blue'
	| 'orange'
	| 'grey'
	| 'vahterus'
	| 'vahterusDark'

export const WorkspaceSwitcher: React.FC<WorkspaceSwitcherProps> = ({
	value,
	options = [],
	isSwitching,
	onSelect,
}) => {
	const navigate = useNavigate()
	const prodBaseDomain = import.meta.env.VITE_PRICING_WEBAPP_BASE_CPQ_URL

	const formattedComapnyName = React.useCallback((name?: string) => {
		if (!name) return ''

		const splitted = name.split(' ')

		if (splitted.length > 1) {
			return `${splitted[0].charAt(0)}${splitted[1].charAt(0)}`
		}

		return `${name.charAt(0).toUpperCase()}${name.charAt(1).toUpperCase()}`
	}, [])

	const currentOption = React.useMemo(() => {
		if (!value) return {label: '', value: ''}

		return options.find(option => option.value === value)
	}, [value, options])

	const formattedTriggerAvatar = React.useCallback(
		(name: string): AvatarColor => {
			if (!value) return 'grey'
			const color: AvatarColor[] = ['green', 'blue', 'orange', 'grey']
			const random = Math.floor(Math.random() * color.length)

			if (name.toLowerCase().includes('vahterus')) {
				return 'vahterus'
			} else return color[random]
		},
		[value],
	)

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild disabled={isSwitching}>
				<SidebarMenuButton
					icon={
						<Avatar
							type="initial"
							shape="square"
							color={formattedTriggerAvatar(currentOption?.label ?? '')}
							size="medium"
							username={currentOption?.label || 'Company Name'}
						/>
					}
					hideTooltip
					label={currentOption?.label || 'Company Name'}
					className="w-full justify-between truncate"
					cy-data="workspaceSwitcher"
				>
					<ChevronDown className="ml-auto min-h-4 min-w-4 h-4 w-4" />
				</SidebarMenuButton>
			</DropdownMenuTrigger>
			<DropdownMenuContent className="w-[300px] p-0 max-h-none">
				<DropdownMenuRadioGroup defaultValue={value}>
					{options.map(workspace => (
						<DropdownMenuRadioItem
							key={workspace.value}
							value={workspace.value}
							label={workspace.label}
							avatar={
								<Avatar
									type="initial"
									shape="square"
									color={formattedTriggerAvatar(workspace.label ?? '')}
									size="medium"
									username={formattedComapnyName(workspace.label)}
								/>
							}
							className="[&>div>div]:items-center"
							onClick={() => onSelect(workspace.value)}
						/>
					))}
					<DropdownMenuSeparator />
				</DropdownMenuRadioGroup>
				<DropdownMenuItem
					label={'Workspace members'}
					onClick={() =>
						navigate(`${prodBaseDomain}/cpq/user/workspace/member`)
					}
					cy-data="workspaceMembers"
				/>
				{options.length > 1 && (
					<DropdownMenuItem
						label={'View all my workspaces'}
						onClick={() => navigate(`${prodBaseDomain}/cpq/user/workspaces`)}
						cy-data="seeAllWorkspaces"
					/>
				)}
			</DropdownMenuContent>
		</DropdownMenu>
	)
}
